<template>
  <div class="kb-row">
    <div>
      <h4 class="kb-row-title">
        Rolling in {{ paradeYear }}
      </h4>
      <p class="kb-row-hint">
        Members roll by default once they pay; red shirts don't until they confirm.
        This switch overrides that and doesn't waive dues.
      </p>
    </div>
    <div class="kb-row-aside">
      <KbSpinner
        v-if="updating"
        label="Saving"
      />
      <span class="kb-row-meta whitespace-nowrap">{{ rollingStatus === 'Yes' ? 'rolling' : 'not rolling' }}</span>
      <ToggleSwitch
        v-model="isRolling"
        :disabled="updating"
        :aria-label="`Rolling in ${paradeYear}`"
        @change="onToggle"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Member } from '~/shared/types/member'
import { isRedshirt } from '~/shared/types/memberType.js'
import {
  getMemberRollingForYear,
  mergeMemberRollingForYear,
  type MemberRollingByYear,
} from '~/shared/utils/memberRolling.js'

interface Props {
  member: Member
}

const props = defineProps<Props>()
const emit = defineEmits<{ updated: [] }>()

const membersStore = useMembersStore()
const toast = useToast()
const { paradeYear } = useParadeYear()

const memberIsRedshirt = computed(() => isRedshirt(props.member))

const rollingStatus = computed(() =>
  getMemberRollingForYear(
    props.member as { rolling_by_year?: MemberRollingByYear },
    paradeYear.value,
    memberIsRedshirt.value ? 'No' : 'Yes',
  ),
)
const isRolling = ref(rollingStatus.value === 'Yes')
const updating = ref(false)

watch(rollingStatus, (v) => {
  if (!updating.value) isRolling.value = v === 'Yes'
})

const onToggle = async () => {
  if (!props.member) return
  updating.value = true
  const newValue: 'Yes' | 'No' = isRolling.value ? 'Yes' : 'No'
  try {
    const merged = mergeMemberRollingForYear(
      props.member as { rolling_by_year?: MemberRollingByYear },
      paradeYear.value,
      newValue,
    )
    const success = await membersStore.updateMember(props.member, { rolling_by_year: merged })
    if (success) {
      toast.add({
        severity: 'success',
        summary: 'Updated',
        detail: newValue === 'Yes'
          ? `${props.member.name || 'Member'} is rolling in ${paradeYear.value}`
          : `${props.member.name || 'Member'} is not rolling in ${paradeYear.value}`,
        life: 3000,
      })
      emit('updated')
    }
    else {
      isRolling.value = !isRolling.value
      toast.add({
        severity: 'error',
        summary: 'Update failed',
        detail: 'Could not update rolling status',
        life: 4000,
      })
    }
  }
  catch (err) {
    isRolling.value = !isRolling.value
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err instanceof Error ? err.message : String(err),
      life: 4000,
    })
  }
  finally {
    updating.value = false
  }
}
</script>
